import { Component, DestroyRef, effect, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { isNotNullish } from '@examdojo/core/util/nullish';
import {
  ChecklistConfig,
  ChecklistItem,
  ChecklistItemId,
  ChecklistItemType,
  ChecklistSection,
  CONDITIONS,
  Feedback,
  FeedbackItem,
  FeedbackResponse,
  MarkschemeInfo,
} from '@examdojo/question';
import { tap } from 'rxjs';
import { DerivativeQuestionReviewType, ReferenceQuestionReviewType } from '../review-item/review-item.model';
import { ConsensusChecklistItemComponent } from './consensus-checklist-item.component';

// For a fully typesafe version we need to wait for https://github.com/microsoft/TypeScript/issues/32063
// const ChecklistConfig = baseConfig as ChecklistConfig;

export enum SubmitButtonText {
  SubmitContinue = 'Submit & Continue',
  ApproveContinue = 'Approve & Continue',
  RejectContinue = 'Reject & Continue',
}

function evaluateCondition(expression: string, context: Record<string, unknown>): boolean {
  try {
    return new Function(...Object.keys(context), `return ${expression};`)(...Object.values(context));
  } catch (error) {
    return false;
  }
}

export interface QuestionReviewFeedbackFormState {
  value: FeedbackResponse | null;
  isValid: boolean;
  submitText: SubmitButtonText;
  allChecklistItemsPassed: boolean;
  canSubmit: boolean;
}

@Component({
  selector: 'dojo-consensus-feedback-form',
  template: `
    <form [formGroup]="form" class="w-full">
      <mat-accordion multi>
        @for (section of visibleSections(); track section.section_id; let i = $index) {
          <mat-expansion-panel [expanded]="i === 0" class="consensus-feedback-form mat-elevation-z0">
            <mat-expansion-panel-header [expandedHeight]="'48px'" [collapsedHeight]="'48px'">
              <mat-panel-title class="flex items-center gap-2">
                <dojo-icon
                  [icon]="isSectionComplete(section) ? 'check-circle' : 'circle-dashed'"
                  [iconClass]="isSectionComplete(section) ? 'text-sky-500' : 'text-neutral-300'"
                  [size]="18"
                  [matTooltip]="isSectionComplete(section) ? 'Section Completed' : 'Section Incomplete'"
                />
                {{ section.section_display_name || section.section_id }}
              </mat-panel-title>
            </mat-expansion-panel-header>
            @for (item of section.checklist; track item.id) {
              @if (isItemVisible(item)) {
                <div class="checklist-item rounded-lg border border-neutral-50 bg-gray-50 p-3">
                  <dojo-consensus-checklist-item
                    [item]="item"
                    [formGroupName]="item.id"
                    [parentFormGroup]="form"
                    [calculatorAllowed]="calculatorAllowed()"
                    [editingAllowed]="editingAllowed()"
                    [shouldShowExtraTextFieldFn]="shouldShowExtraTextField.bind(this)"
                    [isItemVisibleFn]="isItemVisible.bind(this)"
                    [shouldShowFixedToggleFn]="shouldShowFixedToggle.bind(this)"
                    [checkPassConditionFn]="checkPassCondition.bind(this)"
                    (showExplanation)="showExplanation.emit($event)"
                  />
                </div>
              }
            }
          </mat-expansion-panel>
        }
      </mat-accordion>
    </form>
  `,
  styles: [
    `
      ::ng-deep .consensus-feedback-form.mat-expansion-panel {
        .mat-expansion-panel-spacing {
          margin: 0 !important;
        }

        .mat-expansion-panel-header {
          padding: 0 14px !important;
        }

        .mat-expansion-panel-body {
          padding: 0 14px 14px 14px !important;
        }
      }

      .custom-mat-form-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .checklist-item {
        + .checklist-item {
          margin-top: theme('spacing.3');
        }
      }

      .custom-toggle-group {
        display: flex;
        width: 100%;
      }
    `,
  ],
  imports: [
    ReactiveFormsModule,
    MatRadioModule,
    MatButtonToggleModule,
    ConsensusChecklistItemComponent,
    MatExpansionModule,
    MatTooltipModule,
    IconComponent,
  ],
})
export class ConsensusFeedbackFormComponent implements OnInit {
  constructor(
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
  ) {
    effect(() => {
      // TODO: Find a better way to pass the value around and build the form and other pieces
      // based on it.
      this.buildForm();
      this.updateFormValidators();
      this.updateFormState();
    });

    effect(() => {
      const feedbackData = this.feedback();
      if (feedbackData && this.form) {
        this.setData({ feedback: feedbackData, allChecklistItemsPassed: true });
      }
    });

    effect(() => {
      if (this.isReadonly()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  readonly calculatorAllowed = input.required<boolean>();
  readonly activeReviewMode = input.required<DerivativeQuestionReviewType | ReferenceQuestionReviewType>();
  readonly feedback = input<Feedback[]>();
  readonly isReadonly = input(false);

  readonly checklistConfig = input.required<ChecklistConfig>();
  readonly markschemeInfo = input<MarkschemeInfo[]>([]);

  readonly editingAllowed = input(true);
  readonly showFixedToggle = input(true);

  form!: FormGroup;

  readonly formStateChange = output<QuestionReviewFeedbackFormState>();
  readonly ChecklistItemType = ChecklistItemType;
  readonly showExplanation = output<string>();

  submitTextDisplay = signal<SubmitButtonText>(SubmitButtonText.SubmitContinue);

  visibleSections() {
    return this.checklistConfig().sections.filter((section) => section.checklist.length > 0);
  }

  ngOnInit() {
    this.buildForm();

    // Initial setup of validators
    this.updateFormValidators();
    this.updateFormState();
  }

  buildForm() {
    if (this.form) {
      this.form.reset();
    }

    const formControls: Record<string, FormGroup> = {};

    // Process each section and its checklist items
    for (const section of this.checklistConfig().sections) {
      for (const item of section.checklist) {
        // Check if this form control already exists to avoid duplicates
        if (!formControls[item.id]) {
          const itemGroup = this.createItemFormGroup(item);
          formControls[item.id] = itemGroup;
        }
      }
    }

    this.form = this.fb.group(formControls);
    this.form.valueChanges
      .pipe(
        tap(() => {
          this.updateFormValidators();
          this.updateFormState();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  createItemFormGroup(item: ChecklistItem): FormGroup {
    const controls: Record<string, FormControl> = {
      value: this.fb.control(item.type === ChecklistItemType.YesNo ? null : false, { nonNullable: true }),
    };

    return this.fb.group(controls);
  }

  updateFormValidators() {
    // Use a more targeted approach to only update necessary validators
    for (const section of this.checklistConfig().sections) {
      for (const item of section.checklist) {
        this.updateItemValidators(item);
      }
    }
  }

  updateFormState() {
    const isValid = this.form.valid;
    const value = this.getValue();
    const allChecklistItemsPassed = value?.allChecklistItemsPassed ?? false;
    const canSubmit = isValid && this.form.dirty;

    // Check if any item has fixed === false
    const hasUnfixableItems = this.hasUnfixableItems();

    let submitText = SubmitButtonText.SubmitContinue;
    if (isValid && value) {
      if (allChecklistItemsPassed) {
        submitText = SubmitButtonText.ApproveContinue;
      } else if (hasUnfixableItems) {
        submitText = SubmitButtonText.RejectContinue;
      } else {
        submitText = SubmitButtonText.SubmitContinue;
      }
    }

    this.submitTextDisplay.set(submitText);

    this.formStateChange.emit({
      isValid,
      submitText,
      allChecklistItemsPassed,
      canSubmit,
      value,
    });
  }

  updateItemValidators(item: ChecklistItem) {
    const isVisible = this.isItemVisible(item);
    const formGroup = this.form.get(item.id) as FormGroup;

    if (!formGroup) {
      return;
    }

    const valueControl = formGroup.get('value');
    if (valueControl) {
      // Skip updating if validators haven't changed
      const hasRequiredValidator = valueControl.hasValidator(Validators.required);

      if (isVisible && item.type === ChecklistItemType.YesNo && !hasRequiredValidator) {
        valueControl.setValidators([Validators.required]);
        valueControl.updateValueAndValidity({ emitEvent: false }); // Prevent emitting events
      } else if ((!isVisible || item.type !== ChecklistItemType.YesNo) && hasRequiredValidator) {
        valueControl.clearValidators();
        valueControl.updateValueAndValidity({ emitEvent: false }); // Prevent emitting events
      }
    }

    // Handle comment field if it exists
    const commentControl = formGroup.get('comment');
    if (commentControl) {
      const shouldShowTextField = isVisible && this.shouldShowExtraTextField(item);
      if (!shouldShowTextField) {
        commentControl.clearValidators();
        commentControl.updateValueAndValidity({ emitEvent: false }); // Prevent emitting events
      }
    }
  }

  isItemVisible(item: ChecklistItem): boolean {
    if (!item.display) {
      return true;
    }

    const conditionId = item.display;
    const condition = CONDITIONS.find((c) => c.id === conditionId);

    if (!condition) {
      return true;
    }

    // Helper function to get another checklist item's value by ID
    const getChecklistItemValue = (itemId: string): unknown => {
      const control = this.getFormControl(itemId, 'value');
      return control?.value;
    };

    const context = {
      calculatorAllowed: this.calculatorAllowed(),
      value: this.getFormControl(item.id, 'value')?.value,
      getChecklistItemValue, // Add function to get another item's value
      referencedItemId: item.referencedItemId,
      totalSolutionPaths: this.getSolutionPathsForItem(item.id),
      location: item.location,
    };

    return evaluateCondition(condition.expression, context);
  }

  shouldShowExtraTextField(item: ChecklistItem): boolean {
    if (!item.show_extra_text_field) {
      return false;
    }

    const conditionId = item.show_extra_text_field;
    const condition = CONDITIONS.find((c) => c.id === conditionId);

    if (!condition) {
      return false;
    }

    // Helper function to get another checklist item's value by ID
    const getChecklistItemValue = (itemId: string): unknown => {
      const control = this.getFormControl(itemId, 'value');
      return control?.value;
    };

    const context = {
      calculatorAllowed: this.calculatorAllowed(),
      value: this.getFormControl(item.id, 'value')?.value,
      getChecklistItemValue, // Add function to get another item's value
      referencedItemId: item.referencedItemId,
      totalSolutionPaths: this.getSolutionPathsForItem(item.id),
    };

    return evaluateCondition(condition.expression, context);
  }

  shouldShowFixedToggle(item: ChecklistItem): boolean {
    // Don't show fixed toggle if editing is not allowed OR if explicitly disabled
    if (!this.editingAllowed() || !this.showFixedToggle()) {
      return false;
    }

    // Only show fixed toggle if the item has a pass condition and the current answer doesn't meet it
    if (!item.pass_condition) {
      return false;
    }

    const value = this.getFormControl(item.id, 'value')?.value;

    // If no value is set yet, don't show the fixed toggle
    if (value === null || value === undefined) {
      return false;
    }

    // Check if the current value meets the pass condition
    const passesCondition = this.checkPassCondition(item, value);

    // Show fixed toggle only when the answer doesn't meet the pass condition
    return !passesCondition;
  }

  isSectionComplete(section: ChecklistSection): boolean {
    if (!this.form) {
      return false;
    }

    for (const item of section.checklist) {
      // Skip items that aren't visible
      if (!this.isItemVisible(item)) {
        continue;
      }

      const itemGroup = this.form.get(item.id);
      if (!itemGroup) {
        return false;
      }

      const value = itemGroup.get('value')?.value;

      // Check if the item has a value
      if (value === null || value === undefined) {
        return false;
      }

      // Check if the item passes its condition
      if (!this.checkPassCondition(item, value)) {
        // If it doesn't pass, check if fixed toggle is required and filled
        if (this.shouldShowFixedToggle(item)) {
          const fixedValue = itemGroup.get('fixed')?.value;
          if (fixedValue === null || fixedValue === undefined) {
            return false;
          }
        }
      }

      // Check if extra text field is required and filled
      if (this.shouldShowExtraTextField(item)) {
        const commentValue = itemGroup.get('comment')?.value;
        if (!commentValue?.trim()) {
          return false;
        }
      }
    }

    return true;
  }

  getFormControl(itemId: string, controlName = 'value'): FormControl {
    const group = this.form.get(itemId) as FormGroup;
    return group?.get(controlName) as FormControl;
  }

  clearForm() {
    this.form.reset();
    this.updateFormValidators();
  }

  private getValue(): FeedbackResponse | null {
    if (this.form.invalid) {
      return null;
    }

    const formValue = this.form.getRawValue();

    // Create sections one by one with explicit typing
    const feedbackData: Feedback[] = [];
    let allItemsPassed = true; // Initialize as true, will be set to false if any item fails

    for (const section of this.checklistConfig().sections) {
      const feedbackItems: FeedbackItem[] = [];

      for (const item of section.checklist) {
        // Skip items that aren't visible
        if (!this.isItemVisible(item)) {
          continue;
        }

        const itemGroup = formValue[item.id];
        if (!itemGroup) {
          continue;
        }

        const itemValue = itemGroup.value;
        const itemPassed = this.checkPassCondition(item, itemValue);
        if (!itemPassed) {
          allItemsPassed = false;
        }

        const feedbackItem: FeedbackItem = {
          id: this.getOriginalItemId(item.id) as ChecklistItemId,
          value: itemValue,
        };

        if (itemGroup.comment?.trim()) {
          feedbackItem.extra_text_field = itemGroup.comment.trim();
        }

        // Add fixed field only if showFixedToggle is enabled and the item doesn't pass the condition
        if (this.showFixedToggle() && isNotNullish(itemGroup.fixed) && !itemPassed) {
          feedbackItem.fixed = itemGroup.fixed;
        }

        feedbackItems.push(feedbackItem);
      }

      feedbackData.push({
        section_id: section.section_id!,
        section_type: section.section_type,
        checklist: feedbackItems,
      });
    }

    return {
      allChecklistItemsPassed: allItemsPassed,
      feedback: feedbackData,
    };
  }

  // Helper method to check if an item passes its condition
  protected checkPassCondition(item: ChecklistItem, value: unknown): boolean {
    if (!item.pass_condition) {
      return true;
    }

    const conditionId = item.pass_condition;
    const condition = CONDITIONS.find((c) => c.id === conditionId);

    if (!condition) {
      return true;
    }

    // Helper function to get another checklist item's value by ID
    const getChecklistItemValue = (itemId: string): unknown => {
      const control = this.getFormControl(itemId, 'value');
      return control?.value;
    };

    const context = {
      calculatorAllowed: this.calculatorAllowed(),
      value,
      getChecklistItemValue, // Add function to get another item's value
      referencedItemId: item.referencedItemId,
      totalSolutionPaths: this.getSolutionPathsForItem(item.id),
    };

    return evaluateCondition(condition.expression, context);
  }

  /**
   * Sets the form data based on provided feedback response
   * This is the reverse operation of getValue()
   */
  private setData(feedbackData: FeedbackResponse): void {
    if (!this.form || !feedbackData) {
      return;
    }

    // Create a map of original IDs to form control IDs
    const originalIdToFormId = new Map<string, string>();

    for (const section of this.checklistConfig().sections) {
      for (const item of section.checklist) {
        const originalId = this.getOriginalItemId(item.id);
        originalIdToFormId.set(`${section.section_id}_${originalId}`, item.id);
      }
    }

    // Set form values based on feedback data
    for (const sectionFeedback of feedbackData.feedback) {
      for (const itemFeedback of sectionFeedback.checklist) {
        const formItemId = originalIdToFormId.get(`${sectionFeedback.section_id}_${itemFeedback.id}`);
        if (!formItemId) {
          continue;
        }

        const itemGroup = this.form.get(formItemId) as FormGroup;
        if (!itemGroup) {
          continue;
        }

        // Set the main value
        const valueControl = itemGroup.get('value');
        if (valueControl) {
          valueControl.setValue(itemFeedback.value);
        }

        // Set extra text field if it exists
        if (itemFeedback.extra_text_field) {
          let commentControl = itemGroup.get('comment');
          if (!commentControl) {
            // Add comment control if it doesn't exist
            commentControl = this.fb.control(itemFeedback.extra_text_field);
            itemGroup.addControl('comment', commentControl);
          } else {
            commentControl.setValue(itemFeedback.extra_text_field);
          }
        }

        // Set fixed field if it exists
        if (itemFeedback.fixed !== undefined) {
          let fixedControl = itemGroup.get('fixed');
          if (!fixedControl) {
            // Add fixed control if it doesn't exist
            fixedControl = this.fb.control(itemFeedback.fixed);
            itemGroup.addControl('fixed', fixedControl);
          } else {
            fixedControl.setValue(itemFeedback.fixed);
          }
        }
      }
    }

    // Update validators and form state after setting data
    this.updateFormValidators();
    this.form.markAsPristine();
    this.updateFormState();
  }

  private getMarkschemeIndexFromItemId(itemId: string): number | null {
    const match = itemId.match(/_markscheme_(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Gets the solution path count for a specific markscheme based on the item ID
   */
  private getSolutionPathsForItem(itemId: string): number {
    const markschemeIndex = this.getMarkschemeIndexFromItemId(itemId);
    if (markschemeIndex === null || !this.markschemeInfo()[markschemeIndex]) {
      return 0;
    }
    return this.markschemeInfo()[markschemeIndex].markscheme.solution_paths.length;
  }

  /**
   * Strips the unique suffix from item IDs to get the original ID for backend submission
   * Example: "markscheme_correct_marks_markscheme_7" -> "markscheme_correct_marks"
   */
  private getOriginalItemId(itemId: string): string {
    // Remove suffixes like "_markscheme_0", "_markscheme_1", "_image_0", etc.
    return itemId.replace(/_(markscheme|image|table|mcq)_\d+$/, '');
  }

  /**
   * Check if any form item has fixed === false
   */
  private hasUnfixableItems(): boolean {
    if (!this.form) {
      return false;
    }

    for (const section of this.checklistConfig().sections) {
      for (const item of section.checklist) {
        // Skip items that aren't visible
        if (!this.isItemVisible(item)) {
          continue;
        }

        const itemGroup = this.form.get(item.id) as FormGroup;
        if (!itemGroup) {
          continue;
        }

        const value = itemGroup.get('value')?.value;
        const itemPassed = this.checkPassCondition(item, value);

        // If item doesn't pass and has fixed === false, it's unfixable
        if (!itemPassed) {
          const fixedValue = itemGroup.get('fixed')?.value;
          if (fixedValue === false) {
            return true;
          }
        }
      }
    }

    return false;
  }
}
